import { Rect, Text } from "react-konva";
import { useMemo } from "react";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import type { ShirtData, Surface } from "../model/types";

type PrintableAreaProps = {
  surface: Surface;
  shirtData: ShirtData;
  placement?: PlacementFormPosition | undefined;
  variant?: "default" | "drag-preview" | "hovered";
};

const PLACEMENT_LABELS: Record<PlacementFormPosition, string> = {
  front: "Front Center",
  frontLeftChest: "Left Chest",
  back: "Back Center"
};

export function PrintableArea({
  surface,
  shirtData,
  placement,
  variant = "default"
}: PrintableAreaProps) {
  const { isDesktop } = useResponsive();
  const isHovered = variant === "hovered";

  // Calculate printable area coordinates based on surface bounding box and shirt positioning
  const areaData = useMemo(() => {
    const { boundingBox } = surface;
    const { scale, offsetX, offsetY } = shirtData;

    return {
      left: boundingBox.left * scale + offsetX,
      top: boundingBox.top * scale + offsetY,
      width: boundingBox.width * scale,
      height: boundingBox.height * scale
    };
  }, [surface, shirtData]);

  const styles = useMemo(() => {
    const baseStyles = {
      stroke: surface.isDark ? "#FFFFFF" : "#99A1AF",
      strokeWidth: 1,
      fill: isHovered ? "#E5E7EB" : "transparent"
    };

    if (placement === "frontLeftChest") {
      return {
        ...baseStyles,
        dash: [4, 4]
      };
    }

    return baseStyles;
  }, [isHovered, surface.isDark, placement]);

  return (
    <>
      <Rect
        x={areaData.left}
        y={areaData.top}
        width={areaData.width}
        height={areaData.height}
        listening={false}
        {...styles}
      />
      {placement && (
        <Text
          x={areaData.left}
          y={areaData.top + areaData.height - 10}
          width={areaData.width}
          text={PLACEMENT_LABELS[placement]}
          fontSize={isDesktop ? 7 : 6}
          fill={surface.isDark ? "white" : "black"}
          align="center"
          listening={false}
        />
      )}
    </>
  );
}
